<template>
  <div class="group-list-wrapper">
    <!-- Header com Título e Ações -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-1">
      <div>
        <h3 class="font-weight-bolder text-primary mb-25 d-flex align-items-center">
          <feather-icon icon="LayersIcon" size="24" class="mr-50" />
          {{ $t('Grupos de Ativos') }}
        </h3>
        <p class="text-muted font-small-3 mb-0">
          {{ $t('Alarmes configurados em um grupo são aplicados automaticamente a todos os veículos vinculados.') }}
        </p>
      </div>

      <div class="d-flex align-items-center mt-1 mt-md-0" style="gap: 8px;">
        <!-- Botão de Alternar Painel de Filtros -->
        <b-button :variant="showFilters ? 'primary' : 'outline-primary'" size="sm" @click="showFilters = !showFilters"
          class="d-inline-flex align-items-center">
          <feather-icon icon="FilterIcon" size="14" class="mr-50" />
          <span>{{ showFilters ? $t('Ocultar Filtros') : $t('Filtros') }}</span>
          <b-badge v-if="hasAdvancedFilters" variant="light-danger" pill class="ml-50">
            {{ activeAdvancedFiltersCount }}
          </b-badge>
        </b-button>

        <b-button variant="outline-secondary" size="sm" class="btn-icon" @click="refetchData" v-b-tooltip.hover
          :title="$t('Atualizar Lista')">
          <feather-icon icon="RotateCwIcon" size="14" />
        </b-button>

        <b-button variant="primary" size="sm" :to="{ name: 'register-group' }" class="d-inline-flex align-items-center">
          <feather-icon icon="PlusIcon" size="14" class="mr-50" />
          <span>{{ $t('Adicionar Grupo') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Painel de Filtros (Cliente e Subclientes) -->
    <transition name="slide-fade">
      <b-card v-if="showFilters" no-body class="mb-1 border-0 shadow-sm filters-card">
        <b-card-body class="p-1">
          <!-- Cabeçalho do Card de Filtros -->
          <div class="d-flex align-items-center justify-content-between mb-75 pb-50 border-bottom">
            <div class="d-flex align-items-center">
              <feather-icon icon="SlidersIcon" size="15" class="text-primary mr-50" />
              <span class="font-weight-bolder font-small-3">{{ $t('Filtro por Cliente') }}</span>
              <b-badge v-if="hasAdvancedFilters" variant="light-primary" pill class="ml-50 font-small-1">
                {{ activeAdvancedFiltersCount }} {{ $t('ativo(s)') }}
              </b-badge>
            </div>

            <div class="d-flex align-items-center" style="gap: 6px;">
              <b-button v-if="hasAdvancedFilters" variant="flat-danger" size="sm"
                class="py-25 px-50 font-small-2 d-inline-flex align-items-center"
                @click="clearCustomerFilter(); includeSubcustomers = true;" v-b-tooltip.hover
                :title="$t('Redefinir filtros')">
                <feather-icon icon="RotateCcwIcon" size="12" class="mr-25" />
                <span>{{ $t('Redefinir') }}</span>
              </b-button>
              <b-button variant="flat-secondary" size="sm" class="btn-icon p-25" @click="showFilters = false"
                v-b-tooltip.hover :title="$t('Fechar')">
                <feather-icon icon="XIcon" size="14" />
              </b-button>
            </div>
          </div>

          <!-- Conteúdo: Apenas Cliente e Toggle de Subclientes -->
          <b-row class="align-items-start">
            <!-- Campo: Cliente (Autosuggest) -->
            <b-col cols="12" md="7" lg="8" class="mb-1 mb-md-0">
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon icon="BriefcaseIcon" size="13" class="mr-25 text-primary" />
                <span>{{ $t('Cliente') }}</span>
              </label>
              <div class="position-relative customer-autosuggest-wrapper">
                <vue-autosuggest v-model="searchCustomer" :suggestions="filteredOptionsCustomer"
                  :input-props="inputPropsCustomer" :get-suggestion-value="getSuggestionValueCustomer"
                  @selected="selectHandlerCustomer" @input="onInputChangeCustomer">
                  <template slot-scope="{ suggestion }">
                    <div class="p-50 border-bottom d-flex align-items-center justify-content-between">
                      <div>
                        <span class="font-weight-bold d-block text-body">{{ suggestion.item.name }}</span>
                        <small v-if="suggestion.item.ownerName" class="text-muted">
                          {{ $t('Pertence a: ') }} <strong>{{ suggestion.item.ownerName }}</strong>
                        </small>
                      </div>
                      <feather-icon icon="ChevronRightIcon" size="14" class="text-muted" />
                    </div>
                  </template>
                </vue-autosuggest>
                <feather-icon v-if="searchCustomer" icon="XIcon" size="14"
                  class="cursor-pointer position-absolute text-muted clear-customer-icon"
                  @click="clearCustomerFilter" />
              </div>
            </b-col>

            <!-- Campo: Incluir subclientes -->
            <b-col cols="12" md="5" lg="4">
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon icon="GitBranchIcon" size="13" class="mr-25 text-primary" />
                <span>{{ $t('Subclientes') }}</span>
              </label>
              <div class="subcustomers-toggle-box" v-b-tooltip.hover
                :title="includeSubcustomers ? $t('Inclui grupos do cliente selecionado e de todos os seus descendentes') : $t('Inclui apenas grupos do cliente selecionado')">
                <div class="custom-control custom-switch custom-control-primary mb-0">
                  <input type="checkbox" class="custom-control-input" id="includeSubcustomersSwitch"
                    v-model="includeSubcustomers">
                  <label class="custom-control-label font-small-2 font-weight-bold cursor-pointer mb-0"
                    for="includeSubcustomersSwitch">
                    {{ $t('Incluir subclientes') }}
                  </label>
                </div>
                <b-badge :variant="includeSubcustomers ? 'light-success' : 'light-secondary'" pill class="font-small-1">
                  {{ includeSubcustomers ? $t('Sim') : $t('Não') }}
                </b-badge>
              </div>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </transition>

    <!-- Barra de Tags de Filtros Ativos (quando painel de filtros está recolhido) -->
    <div v-if="hasActiveFilters && !showFilters"
      class="px-1 py-75 border-bottom bg-light-secondary d-flex flex-wrap align-items-center mb-1 rounded shadow-xs"
      style="gap: 6px;">
      <small class="font-weight-bold text-muted mr-50">{{ $t('Filtros ativos') }}:</small>
      <b-badge v-if="searchQuery" variant="light-primary" pill class="d-inline-flex align-items-center">
        <feather-icon icon="LayersIcon" size="12" class="mr-25" />
        {{ $t('Nome') }}: {{ searchQuery }}
        <feather-icon icon="XIcon" size="10" class="ml-25 cursor-pointer" @click="searchQuery = ''" />
      </b-badge>
      <b-badge v-if="customerId" variant="light-primary" pill class="d-inline-flex align-items-center">
        <feather-icon icon="BriefcaseIcon" size="12" class="mr-25" />
        {{ $t('Cliente') }}: {{ searchCustomer }}
        <feather-icon icon="XIcon" size="10" class="ml-25 cursor-pointer" @click="clearCustomerFilter" />
      </b-badge>
      <b-badge v-if="includeSubcustomers === false" variant="light-warning" pill
        class="d-inline-flex align-items-center">
        {{ $t('Apenas cliente direto (sem subclientes)') }}
        <feather-icon icon="XIcon" size="10" class="ml-25 cursor-pointer" @click="includeSubcustomers = true" />
      </b-badge>
      <b-link class="font-small-2 font-weight-bold ml-50 text-danger" @click="clearFilters">
        {{ $t('Limpar todos') }}
      </b-link>
    </div>

    <!-- Table Container Card -->
    <b-card no-body class="mb-0 shadow-sm border-0">
      <div class="m-1">
        <!-- Table Top Controls -->
        <b-row class="align-items-center">
          <!-- Per Page Selector -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label class="mr-50 mb-0 font-small-3 text-muted">{{ $t('Exibir') }}</label>
            <v-select v-model="perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="perPageOptions"
              :clearable="false" class="per-page-selector d-inline-block mr-50" />
            <label class="mb-0 font-small-3 text-muted">{{ $t('registros') }}</label>
          </b-col>

          <!-- Search Input -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-input-group size="sm" class="search-input-group">
                <b-input-group-prepend is-text>
                  <feather-icon icon="SearchIcon" size="14" class="text-muted" />
                </b-input-group-prepend>
                <b-form-input v-model="searchQuery" :placeholder="$t('Buscar grupo por nome...')" debounce="400" trim />
                <template #append v-if="searchQuery">
                  <b-button variant="outline-secondary" size="sm" @click="searchQuery = ''">
                    <feather-icon icon="XIcon" size="12" />
                  </b-button>
                </template>
              </b-input-group>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Table Component -->
      <b-table ref="refGroupListTable" class="position-relative mb-0 group-list-table" :items="fetchAllGroup" responsive
        :fields="tableColumns" primary-key="id" :sort-by.sync="sortBy" show-empty :sort-desc.sync="isSortDirDesc"
        :busy="isTableBusy">
        <!-- Table Busy Skeleton -->
        <template #table-busy>
          <div class="py-2 px-1">
            <div v-for="n in 4" :key="'skeleton-row-' + n" class="p-1 mb-50 rounded bg-light-secondary">
              <b-row class="align-items-center">
                <b-col cols="4"><b-skeleton width="140px" height="18px" /></b-col>
                <b-col cols="3"><b-skeleton width="100px" height="16px" /></b-col>
                <b-col cols="3"><b-skeleton width="70px" height="20px" /></b-col>
                <b-col cols="2" class="text-right"><b-skeleton width="30px" height="24px" /></b-col>
              </b-row>
            </div>
          </div>
        </template>

        <!-- Column: Name -->
        <template #head(name)>
          {{ $t('Grupo') }}
        </template>
        <template #cell(name)="data">
          <div class="d-flex align-items-center py-25">
            <b-avatar variant="light-primary" size="34px" rounded class="mr-1">
              <feather-icon icon="LayersIcon" size="16" />
            </b-avatar>
            <div>
              <b-link :to="{ name: 'register-group', params: { id: data.item.id } }"
                class="font-weight-bolder text-body d-block text-hover-primary">
                {{ data.item.name }}
              </b-link>
              <small class="text-muted d-block text-truncate" style="max-width: 250px;">
                {{ data.item.description || $t('Sem descrição informada') }}
              </small>
            </div>
          </div>
        </template>

        <!-- Column: Customer Name -->
        <template #head(customerName)>
          {{ $t('Cliente') }}
        </template>
        <template #cell(customerName)="data">
          <div class="d-flex align-items-center">
            <feather-icon icon="BriefcaseIcon" size="14" class="mr-50 text-muted" />
            <span class="font-small-3 text-body">{{ data.item.customerName || '-' }}</span>
          </div>
        </template>

        <!-- Column: Qtd Assets -->
        <template #head(assetsCount)>
          {{ $t('Ativos Vinculados') }}
        </template>
        <template #cell(assetsCount)="data">
          <b-badge :variant="data.item.assetsCount > 0 ? 'light-primary' : 'light-secondary'" pill
            class="px-75 py-50 font-small-2 font-weight-bold d-inline-flex align-items-center">
            <feather-icon icon="TruckIcon" size="13" class="mr-25" />
            <span>{{ data.item.assetsCount }} {{ data.item.assetsCount === 1 ? $t('ativo') : $t('ativos') }}</span>
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #head(actions)>
          <span class="text-right d-block">{{ $t(selectedAssetId ? 'Selecionar' : 'Ações') }}</span>
        </template>
        <template #cell(actions)="data">
          <div class="d-flex align-items-center justify-content-end">
            <!-- Modo Seleção de Ativo -->
            <b-button variant="primary" size="sm" v-if="selectedAssetId"
              @click="bindAssetToGroup(selectedAssetId, data.item.id)" class="d-inline-flex align-items-center">
              <feather-icon icon="CheckSquareIcon" size="14" class="mr-25" />
              <span>{{ $t('Vincular') }}</span>
            </b-button>

            <!-- Menu com 3 pontos verticais contendo todas as ações -->
            <b-dropdown v-else variant="link" no-caret toggle-class="p-0" :right="$store.state.appConfig.isRTL">
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
              </template>

              <!-- Editar Grupo -->
              <b-dropdown-item :to="{ name: 'register-group', params: { id: data.item.id } }">
                <feather-icon icon="EditIcon" class="mr-50 text-primary" />
                <span class="align-middle">{{ $t('Editar') }}</span>
              </b-dropdown-item>

              <!-- Adicionar Ativo no Grupo -->
              <b-dropdown-item @click="openModalBind(data.item.id, data.item.name)">
                <feather-icon icon="PlusCircleIcon" class="mr-50 text-success" />
                <span class="align-middle">{{ $t('Adicionar ativo no grupo') }}</span>
              </b-dropdown-item>

              <b-dropdown-divider />

              <!-- Excluir Grupo -->
              <b-dropdown-item @click="modalDelete(data.item.id, data.item.name)" class="text-danger">
                <feather-icon icon="Trash2Icon" class="mr-50 text-danger" />
                <span class="align-middle text-danger">{{ $t('Excluir') }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>

        <!-- Empty State in Table -->
        <template #empty>
          <div class="text-center py-4 px-2">
            <b-avatar variant="light-primary" size="56px" class="mb-1">
              <feather-icon icon="LayersIcon" size="28" class="text-primary" />
            </b-avatar>
            <h5 class="font-weight-bolder mb-50">{{ $t('Nenhum grupo encontrado') }}</h5>
            <p class="text-muted font-small-3 mb-1 mx-auto" style="max-width: 400px;">
              {{ hasActiveFilters
                ? $t('Nenhum resultado corresponde aos filtros aplicados. Tente ajustar os parâmetros ou limpar os
              filtros.')
              : $t('Crie grupos para reunir ativos da mesma operação e definir limites de velocidade, consumo e
              horários.')
                }}
            </p>
            <b-button v-if="hasActiveFilters" variant="outline-primary" size="sm" @click="clearFilters">
              {{ $t('Limpar Filtros') }}
            </b-button>
            <b-button v-else variant="primary" size="sm" :to="{ name: 'register-group' }"
              class="d-inline-flex align-items-center">
              <feather-icon icon="PlusIcon" size="14" class="mr-50" />
              <span>{{ $t('Criar Primeiro Grupo') }}</span>
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Table Footer: Pagination & Counts -->
      <div class="mx-1 my-1 border-top pt-1">
        <b-row class="align-items-center">
          <b-col cols="12" sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start mb-1 mb-sm-0">
            <span class="text-muted font-small-3">
              {{ $t('Exibindo') }} <strong>{{ dataMeta.from }}</strong> {{ $t('a') }} <strong>{{ dataMeta.to }}</strong>
              {{
                $t('de') }} <strong>{{ dataMeta.of }}</strong> {{ $t('grupos') }}
            </span>
          </b-col>

          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination v-model="currentPage" :total-rows="totalGroup" :per-page="perPage" first-number last-number
              class="mb-0" prev-class="prev-item" next-class="next-item">
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Modal Adicionar Ativo ao Grupo -->
    <b-modal v-model="showModal" no-close-on-backdrop id="modal-cadastro" size="md" centered
      :title="$t('Vincular Ativo ao Grupo')" title-class="font-weight-bolder text-primary" hide-footer
      @hide="limparFormCadastro">
      <div class="mb-1" v-if="targetGroupName">
        <span class="text-muted font-small-2 d-block">{{ $t('Grupo Selecionado') }}:</span>
        <b-badge variant="light-primary" class="font-small-2 py-50 px-75">
          <feather-icon icon="LayersIcon" size="12" class="mr-25" />
          {{ targetGroupName }}
        </b-badge>
      </div>

      <p class="font-small-3 text-muted mb-1">
        {{ $t('Digite ao menos 3 caracteres da placa ou chassi para localizar o ativo desejado.') }}
      </p>

      <b-form-group :label="$t('Selecione o Ativo')" label-for="asset-select">
        <vue-autosuggest v-model="asset.name" :suggestions="filteredOptions" :input-props="inputProps"
          :get-suggestion-value="getSuggestionValue" @selected="selectHandler" @input="onInputChange">
          <template slot-scope="{ suggestion }">
            <div class="p-50 border-bottom d-flex align-items-center justify-content-between">
              <div>
                <span class="font-weight-bold d-block">
                  <feather-icon icon="TruckIcon" size="14" class="mr-25 text-primary" />
                  {{ suggestion.item.plate || suggestion.item.chassisNumber }}
                </span>
                <small v-if="suggestion.item.nickname" class="text-muted">
                  {{ suggestion.item.nickname }}
                </small>
              </div>
              <small v-if="suggestion.item.modelName" class="text-muted">
                {{ suggestion.item.manufacturerName }} {{ suggestion.item.modelName }}
              </small>
            </div>
          </template>
        </vue-autosuggest>
      </b-form-group>

      <div v-if="asset.id"
        class="alert alert-light-success p-75 mb-1 d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <feather-icon icon="CheckCircleIcon" size="16" class="text-success mr-50" />
          <span class="font-small-3 font-weight-bold">{{ $t('Ativo Selecionado') }}: {{ asset.name }}</span>
        </div>
        <feather-icon icon="XIcon" size="14" class="cursor-pointer text-muted" @click="limparAssetSelecionado" />
      </div>

      <div class="d-flex justify-content-end mt-2" style="gap: 8px;">
        <b-button variant="outline-secondary" size="sm" @click="showModal = false">
          {{ $t('Cancelar') }}
        </b-button>
        <b-button variant="primary" size="sm" :disabled="!asset.id || isBinding" @click="bindGroup"
          class="d-inline-flex align-items-center">
          <b-spinner v-if="isBinding" small class="mr-50" />
          <feather-icon v-else icon="PlusIcon" size="14" class="mr-50" />
          <span>{{ isBinding ? $t('Adicionando...') : $t('Confirmar Vínculo') }}</span>
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BPagination,
  BModal,
  BSkeleton,
  BInputGroup,
  BInputGroupPrepend,
  BFormGroup,
  BSpinner,
} from 'bootstrap-vue'

import { VueAutosuggest } from 'vue-autosuggest'
import store from '@/store'
import { ref, onUnmounted } from '@vue/composition-api'
import managementStoreModule from '../managementStoreModule'
import useGroupList from './useGroupList'
import vSelect from 'vue-select'

/** Helper de debounce leve */
const debounce = (fn, delay = 400) => {
  let timeout
  return function (...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => fn.apply(this, args), delay)
  }
}

export default {
  props: ["selectedAssetId"],
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BPagination,
    BModal,
    vSelect,
    VueAutosuggest,
    BSkeleton,
    BInputGroup,
    BInputGroupPrepend,
    BFormGroup,
    BSpinner,
  },
  data() {
    return {
      showFilters: false,
      showModal: false,
      isBinding: false,
      targetGroupName: '',
      asset: {
        name: '',
        id: ''
      },
      filteredOptions: [],
      groupId: '',

      // Filtro de Cliente via Autosuggest
      filteredOptionsCustomer: [],
      selectedCustomer: null,
    }
  },
  computed: {
    inputProps() {
      return {
        id: 'autosuggest__input_modal',
        class: 'form-control',
        placeholder: this.$t('Digite a placa ou chassi...'),
        autocomplete: 'off',
      }
    },
    inputPropsCustomer() {
      return {
        id: 'autosuggest__customer_input',
        class: 'form-control form-control-sm',
        placeholder: this.$t('Todos os clientes (ou busque por nome)...'),
        autocomplete: 'off',
      }
    },
  },
  methods: {
    // --- Autosuggest de Ativo (Modal) ---
    getSuggestionValue(option) {
      if (!option) return ''
      if (option.item) {
        return option.item.plate || option.item.chassisNumber || ''
      }
      return typeof option === 'string' ? option : ''
    },

    selectHandler(option) {
      if (!option || !option.item) return
      this.asset.id = option.item.id
      this.asset.name = option.item.plate ? option.item.plate : option.item.chassisNumber
      this.filteredOptions = []
    },

    onInputChange(text) {
      if (text === null || text === undefined) return

      if (typeof text === 'object' || text === '[object Object]') {
        this.asset.name = ''
        this.asset.id = ''
        this.filteredOptions = []
        return
      }

      if (this.asset.id && text !== this.asset.name) {
        this.asset.id = ''
      }

      if (text.length < 3) {
        this.filteredOptions = []
        return
      }

      store.dispatch('app-management/fetchAssets', {
        q: text,
        excludeGrouped: true
      })
        .then(response => {
          this.filteredOptions = [{
            data: response.data.data,
          }]
        })
        .catch(() => { })
    },

    limparAssetSelecionado() {
      this.asset = {
        name: '',
        id: ''
      }
      this.filteredOptions = []
    },

    // --- Autosuggest de Cliente (Filtro) ---
    getSuggestionValueCustomer(option) {
      if (!option) return ''
      if (option.item) return option.item.name || ''
      return typeof option === 'string' ? option : ''
    },

    selectHandlerCustomer(option) {
      if (!option || !option.item) return
      this.selectedCustomer = option.item
      this.searchCustomer = option.item.name
      this.customerId = option.item.id
      this.filteredOptionsCustomer = []
    },

    _fetchCustomerSuggestions: debounce(function (text) {
      store.dispatch('app-management/fetchCustomers', { q: text })
        .then(response => {
          this.filteredOptionsCustomer = [{
            data: response.data.data,
          }]
        })
        .catch(() => { })
    }, 350),

    onInputChangeCustomer(text) {
      if (text === null || text === undefined) return

      if (typeof text === 'object' || text === '[object Object]') {
        this.clearCustomerFilter()
        return
      }

      this.searchCustomer = text

      if (this.selectedCustomer && text !== this.selectedCustomer.name) {
        this.customerId = ''
        this.selectedCustomer = null
      }

      if (!text || text.length < 2) {
        this.customerId = ''
        this.filteredOptionsCustomer = []
        return
      }

      if (this.selectedCustomer && text === this.selectedCustomer.name) {
        return
      }

      this._fetchCustomerSuggestions(text)
    },

    clearCustomerFilter() {
      this.searchCustomer = ''
      this.customerId = ''
      this.selectedCustomer = null
      this.filteredOptionsCustomer = []
    },

    clearFilters() {
      this.clearAllFilters()
      this.clearCustomerFilter()
    },

    openModalBind(id, name = '') {
      this.showModal = true
      this.groupId = id
      this.targetGroupName = name
    },

    limparFormCadastro() {
      this.asset = {
        name: '',
        id: ''
      }
      this.groupId = ''
      this.targetGroupName = ''
      this.filteredOptions = []
      this.isBinding = false
    },

    bindGroup() {
      this.bindAssetToGroup(this.asset.id, this.groupId)
    },

    bindAssetToGroup(assetId, groupId) {
      this.isBinding = true
      store.dispatch(`${this.MANAGEMENT_APP_STORE_MODULE_NAME}/bindAssetToAssetGroup`, {
        assetId: assetId,
        assetGroupId: groupId
      })
        .then(() => {
          this.$swal({
            position: 'top-end',
            icon: 'success',
            title: this.$t('Ativo adicionado com sucesso ao grupo!'),
            showConfirmButton: false,
            timer: 2500,
            customClass: { confirmButton: 'btn btn-primary' },
            buttonsStyling: false,
          })
          this.showModal = false
          this.refetchData()
          this.$emit("bind")
        })
        .catch(() => {
          this.$swal(this.$t('Erro'), this.$t('Não foi possível vincular o ativo.'), 'error')
        })
        .finally(() => {
          this.isBinding = false
        })
    },

    modalDelete(myId, groupName = '') {
      this.$swal({
        title: this.$t('Excluir Grupo?'),
        text: groupName
          ? `${this.$t('Tem certeza que deseja excluir o grupo')} "${groupName}"?`
          : this.$t('Esta ação não pode ser desfeita.'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Sim, excluir'),
        cancelButtonText: this.$t('Cancelar'),
        customClass: {
          confirmButton: 'btn btn-danger mr-1',
          cancelButton: 'btn btn-outline-secondary',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.deletAassetGroup(myId)
        }
      })
    },

    deletAassetGroup(myId) {
      store.dispatch('app-management/deletAassetGroup', { id: myId })
        .then(() => {
          this.$swal({
            position: 'top-end',
            icon: 'success',
            title: this.$t('Grupo excluído com sucesso!'),
            showConfirmButton: false,
            timer: 2500,
            customClass: { confirmButton: 'btn btn-primary' },
            buttonsStyling: false,
          })
          this.refetchData()
        })
        .catch(() => {
          this.$swal(this.$t('Erro'), this.$t('Não foi possível excluir o grupo.'), 'error')
        })
    }
  },
  setup() {
    const MANAGEMENT_APP_STORE_MODULE_NAME = 'app-management'

    if (!store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) {
      store.registerModule(MANAGEMENT_APP_STORE_MODULE_NAME, managementStoreModule)
    }

    onUnmounted(() => {
      if (store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(MANAGEMENT_APP_STORE_MODULE_NAME)
      }
    })

    const {
      fetchAllGroup,
      tableColumns,
      perPage,
      currentPage,
      totalGroup,
      dataMeta,
      perPageOptions,
      searchQuery,
      customerId,
      searchCustomer,
      includeSubcustomers,
      hasActiveFilters,
      activeFiltersCount,
      hasAdvancedFilters,
      activeAdvancedFiltersCount,
      clearAllFilters,
      sortBy,
      isSortDirDesc,
      refGroupListTable,
      refetchData,
      isTableBusy,
    } = useGroupList()

    return {
      fetchAllGroup,
      tableColumns,
      perPage,
      currentPage,
      totalGroup,
      dataMeta,
      perPageOptions,
      searchQuery,
      customerId,
      searchCustomer,
      includeSubcustomers,
      hasActiveFilters,
      activeFiltersCount,
      hasAdvancedFilters,
      activeAdvancedFiltersCount,
      clearAllFilters,
      sortBy,
      isSortDirDesc,
      refGroupListTable,
      refetchData,
      MANAGEMENT_APP_STORE_MODULE_NAME,
      isTableBusy,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';

.per-page-selector {
  width: 90px;
}

.search-input-group {
  max-width: 320px;
}
</style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.group-list-wrapper {
  .text-hover-primary:hover {
    color: #7367f0 !important;
  }

  .filters-card {
    border-radius: 0.5rem;
  }
}

.customer-autosuggest-wrapper {
  .clear-customer-icon {
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 4;
  }
}

.subcustomers-toggle-box {
  height: 2.714rem;
  padding: 0 0.85rem;
  background-color: rgba(115, 103, 240, 0.04);
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;

  &:hover {
    border-color: #7367f0;
  }
}

.dark-layout {
  .subcustomers-toggle-box {
    background-color: #283046;
    border-color: #404656;

    &:hover {
      border-color: #7367f0;
    }
  }
}

// Garantir que o dropdown não seja cortado quando houver poucos resultados
::v-deep .table-responsive {
  overflow: visible !important;
  min-height: 180px;
}

::v-deep .b-table-wrapper {
  overflow: visible !important;
}

::v-deep .dropdown-menu {
  z-index: 1050 !important;
  position: absolute !important;
}

.group-list-table {
  min-height: 180px;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.25s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-8px);
  opacity: 0;
}
</style>
